import { type ColumnDef, DataTable, Empty, EmptyTitle } from "@aircall/ds";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import type { RowData } from "./table/pages/conversation_center/components/Table/Table.decl";
import { pickColumns } from "./table/states/columns/columns";
import type { ColumnType } from "./table/states/columns/columns.decl";
import { mapToRows } from "./table/states/columns/columns.utilities";
import { useDevConversations } from "./useDevConversations";

// Adapts the ported conversation-center-ext ColumnType configs to the DS
// DataTable's tanstack ColumnDef, verbatim from CC's TableContent.toColumnDefs.
function toColumnDefs(columns: ColumnType[]): ColumnDef<RowData>[] {
  return columns.map((column) => ({
    id: column.id,
    header: () => column.label,
    cell: ({ row }) =>
      column.renderer(
        row.original,
        row.original[column.id as keyof RowData],
        row.index
      ),
  }));
}

// Dev-only landing page rendered at "/" (see AppRoutes, gated by isDev()).
// Renders a faithful port of conversation-center-ext's calls table so the local
// dev server is browsable; clicking a row opens that call at /calls/:id/:type.
export function DevHome() {
  const navigate = useNavigate();
  const { conversations, error, hasMore, loadingState, loadMore } =
    useDevConversations();

  const rows = useMemo(
    () => mapToRows(conversations, false, true),
    [conversations]
  );

  // Feature columns on (sentiment + csat); drop CC's preview column - it opens
  // CC's PreviewDrawer, whereas here the row click already opens the call.
  const columns = useMemo(
    () =>
      toColumnDefs(
        pickColumns({
          isSearchTriggered: false,
          hasSentimentAnalysisFeature: true,
          hasCsatFeature: true,
        }).filter((column) => column.id !== "preview")
      ),
    []
  );

  return (
    <div className="flex flex-col h-screen min-h-0 bg-white p-4">
      <DataTable<RowData>
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.callId)}
        loadingState={loadingState}
        onRowClick={(row) =>
          navigate(
            `/calls/${row.callId}/${row.type === "VOICEMAIL" ? "voicemail" : "recording"}`
          )
        }
        onFetchMore={loadMore}
        hasMore={hasMore}
        fillHeight
        emptyState={
          <Empty>
            <EmptyTitle>
              {error
                ? `Failed to load calls: ${error.message}`
                : "No calls found"}
            </EmptyTitle>
          </Empty>
        }
      />
    </div>
  );
}
